import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Clock, Medal } from 'lucide-react';

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Fun username generation
const generateUsername = () => {
  const adjectives = ['Speedy', 'Clever', 'Quick', 'Swift', 'Mighty', 'Epic', 'Ninja', 'Super', 'Mega', 'Ultra', 'Pixel', 'Digital', 'Cyber', 'Neon', 'Quantum'];
  const nouns = ['Explorer', 'Hunter', 'Mapper', 'Wizard', 'Runner', 'Master', 'Champion', 'Seeker', 'Scout', 'Hacker', 'Wanderer', 'Voyager', 'Pioneer'];
  const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const num = Math.floor(Math.random() * 100);
  return `${adj}${noun}${num}`;
};

const LeaderboardEntry = ({ rank, time, isCurrentUser, username, delay, highlight }) => (
  <motion.div 
    className={`flex items-center justify-between p-4 rounded-lg ${
      isCurrentUser ? 'bg-[#ECFF0F]/20' : 'bg-white/10'
    } ${highlight ? 'scale-105 shadow-lg' : ''}`}
    initial={{ opacity: 0, x: isCurrentUser ? -100 : 0, y: 20 }}
    animate={{ 
      opacity: 1, 
      x: 0, 
      y: 0,
      scale: highlight ? 1.05 : 1,
    }}
    transition={{ 
      delay: delay,
      duration: 0.5,
      type: "spring",
      stiffness: 100,
      damping: 15
    }}
  >
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-full ${
        isCurrentUser ? 'bg-[#ECFF0F]/30' : 'bg-black/10'
      } flex items-center justify-center`}>
        {rank === 1 ? (
          <Trophy className="w-4 h-4 text-[#FFD700]" />
        ) : rank === 2 ? (
          <Medal className="w-4 h-4 text-[#C0C0C0]" />
        ) : rank === 3 ? (
          <Medal className="w-4 h-4 text-[#CD7F32]" />
        ) : (
          <span className="text-sm font-medium">{rank}</span>
        )}
      </div>
      <div className="flex flex-col">
        <span className={`text-lg ${isCurrentUser ? 'font-bold' : ''}`}>
          {isCurrentUser ? 'You' : username}
        </span>
        {rank <= 3 && !isCurrentUser && (
          <span className="text-xs text-black/50">SOOT Master</span>
        )}
      </div>
    </div>
    <div className="flex items-center gap-2">
      <Clock className="w-4 h-4" />
      <span className="text-lg font-medium">{formatTime(time)}</span>
    </div>
  </motion.div>
);

const WinScreen = ({ onLearnMore, onPlayDifferentLevel, elapsedTime }) => {
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(0);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const leaderboardRef = useRef(null);

  useEffect(() => {
    // Generate mock leaderboard times with realistic distribution
    const generateLeaderboardTimes = (playerTime) => {
      const entries = [];
      
      // World records (35-45 seconds)
      for (let i = 0; i < 3; i++) {
        entries.push({
          time: 35 + (Math.random() * 10),
          isCurrentUser: false,
          username: generateUsername()
        });
      }
      
      // Expert times (45-60 seconds)
      for (let i = 0; i < 4; i++) {
        entries.push({
          time: 45 + (Math.random() * 15),
          isCurrentUser: false,
          username: generateUsername()
        });
      }
      
      // Good times (60-90 seconds)
      for (let i = 0; i < 4; i++) {
        entries.push({
          time: 60 + (Math.random() * 30),
          isCurrentUser: false,
          username: generateUsername()
        });
      }
      
      // Average times (90-120 seconds)
      for (let i = 0; i < 3; i++) {
        entries.push({
          time: 90 + (Math.random() * 30),
          isCurrentUser: false,
          username: generateUsername()
        });
      }
      
      // Beginner times (120-180 seconds)
      for (let i = 0; i < 2; i++) {
        entries.push({
          time: 120 + (Math.random() * 60),
          isCurrentUser: false,
          username: generateUsername()
        });
      }
      
      // Generate a competitive time for the player (between 37-50 seconds)
      const fakePlayerTime = 37 + (Math.random() * 13);
      entries.push({
        time: fakePlayerTime,
        isCurrentUser: true,
        username: 'You'
      });

      // Sort by time
      return entries.sort((a, b) => a.time - b.time);
    };

    const entries = generateLeaderboardTimes(elapsedTime);
    const playerIndex = entries.findIndex(entry => entry.isCurrentUser);
    
    // Animated reveal sequence
    setTimeout(() => {
      setShowLeaderboard(true);
      setLeaderboard(entries);
      
      // Animate to player's position after a delay
      setTimeout(() => {
        setHighlightedIndex(playerIndex);
        if (leaderboardRef.current) {
          const entry = leaderboardRef.current.children[playerIndex];
          entry?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 2000);
    }, 1500);

    setUserRank(playerIndex + 1);
  }, [elapsedTime]);

  return (
    <motion.div 
      className="fixed inset-0 flex flex-col items-center justify-start bg-[#ECFF0F] pt-16 pb-8 px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div 
        className="text-7xl font-bold mb-8 text-black text-center"
        style={{ fontFamily: 'ABC Diatype Rounded Medium' }}
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        YOU WIN!!!
      </motion.div>

      <AnimatePresence>
        {showLeaderboard && (
          <motion.div 
            className="bg-black/10 rounded-2xl p-6 mb-6 w-full max-w-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2">Your Time: {formatTime(elapsedTime)}</h2>
              <p className="text-lg">Rank #{userRank} on the leaderboard</p>
            </div>

            <div 
              ref={leaderboardRef}
              className="space-y-3 max-h-[40vh] overflow-y-auto pr-2 rounded-lg custom-scrollbar"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent'
              }}
            >
              {leaderboard.map((entry, index) => (
                <LeaderboardEntry
                  key={index}
                  rank={index + 1}
                  time={entry.time}
                  isCurrentUser={entry.isCurrentUser}
                  username={entry.username}
                  delay={entry.isCurrentUser ? 2 : 0.1 * index}
                  highlight={highlightedIndex === index}
                />
              ))}
            </div>

          </motion.div>
        )}
      </AnimatePresence>

      <motion.div 
        className="flex flex-col gap-4 mt-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <motion.button
          onClick={onLearnMore}
          className="px-8 py-3 bg-black text-[#ECFF0F] rounded-lg text-xl font-medium hover:bg-gray-900 transition-colors"
          style={{ fontFamily: 'ABC Diatype Rounded Medium' }}
          whileHover={{ scale: 1.05 }}
        >
          Learn more about SOOT
        </motion.button>

        <motion.button
          onClick={onPlayDifferentLevel}
          className="px-8 py-3 border-2 border-black text-black rounded-lg text-xl font-medium hover:bg-black/10 transition-colors"
          style={{ fontFamily: 'ABC Diatype Rounded Medium' }}
          whileHover={{ scale: 1.05 }}
        >
          Play a different Level
        </motion.button>
      </motion.div>

      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.2);
            border-radius: 3px;
          }
        `}
      </style>
    </motion.div>
  );
};

export default WinScreen;